import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface PayerRate {
    id: string;
    payerId: string | null;
    payerName: string;
    serviceTypeId: string | null;
    allowRate: number;
    effectiveDate: string | null;
    endDate: string | null;
}

export interface PayerRateWritePayload {
    payerId?: string | null;
    payerName?: string;
    serviceTypeId?: string;
    allowRate?: number;
    effectiveDate?: string | null;
    endDate?: string | null;
}

export function usePayerRates(serviceTypeId?: string) {
    return useQuery({
        queryKey: ['payer-rates', serviceTypeId ?? 'all'] as const,
        queryFn: async () => {
            const res = await api.get<PayerRate[]>('/payer-rates', {
                params: serviceTypeId ? { serviceTypeId } : undefined,
            });
            return res.data;
        },
        staleTime: 30_000,
        enabled: serviceTypeId === undefined ? true : Boolean(serviceTypeId),
    });
}

export function useCreatePayerRate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: PayerRateWritePayload) => {
            const res = await api.post<PayerRate>('/payer-rates', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['payer-rates'] }),
    });
}

export function useUpdatePayerRate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: PayerRateWritePayload }) => {
            const res = await api.patch<PayerRate>(`/payer-rates/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['payer-rates'] }),
    });
}

export function useDeletePayerRate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/payer-rates/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['payer-rates'] }),
    });
}
